/**
 * LkModal — lightweight reusable modal utility (LegalKPI shared components).
 *
 * HTML contract:
 *   - Backdrop:   <div class="lk-modal-backdrop" id="myModalId" style="display:none;">
 *   - Open:       <button data-lk-modal="myModalId">
 *   - Close:      <button class="lk-modal-close"> OR [data-lk-modal-close] OR ESC OR backdrop click
 *   - Dirty watch: add data-lk-dirty-watch to .lk-modal-body — any input/change marks modal dirty
 *   - Locked:     add data-lk-modal-locked to the backdrop: ESC, backdrop click and close
 *                 buttons are ignored, and only its own script closes it, with forceClose
 *
 * JS API (for programmatic control):
 *   LkModal.open('myModalId')
 *   LkModal.close('myModalId')        — respects dirty state
 *   LkModal.forceClose('myModalId')   — closes without dirty check
 *   LkModal.setDirty('myModalId', true|false)
 */
const LkModal = (() => {
    const _state = {};        // { [modalId]: { dirty: bool } }
    let _pendingClose = null; // modal id awaiting discard confirmation

    function open(id) {
        const el = document.getElementById(id);
        if (!el) return;
        _state[id] = _state[id] || { dirty: false };
        el.style.display = 'grid';
        document.body.style.overflow = 'hidden';
    }

    function close(id) {
        if (document.getElementById(id)?.hasAttribute('data-lk-modal-locked')) {
            return;
        }

        if (_state[id]?.dirty) {
            _showDiscard(id);
            return;
        }
        _doClose(id);
    }

    function forceClose(id) {
        _doClose(id);
    }

    function setDirty(id, val) {
        _state[id] = _state[id] || {};
        _state[id].dirty = !!val;
    }

    // ── Internal ──────────────────────────────────────────────────────────

    function _doClose(id) {
        const el = document.getElementById(id);
        if (!el) return;
        el.style.display = 'none';
        document.body.style.overflow = '';
        if (_state[id]) _state[id].dirty = false;
        el.querySelectorAll('form').forEach(f => f.reset());
        el.dispatchEvent(new CustomEvent('lk:close'));
    }

    function _showDiscard(id) {
        _pendingClose = id;
        const d = document.getElementById('lkDiscardBackdrop');
        if (d) d.style.display = 'grid';
    }

    function _hideDiscard() {
        const d = document.getElementById('lkDiscardBackdrop');
        if (d) d.style.display = 'none';
        _pendingClose = null;
    }

    // ── Auto-wire on DOM ready ────────────────────────────────────────────

    document.addEventListener('DOMContentLoaded', () => {

        // Delegated click handler
        document.addEventListener('click', (e) => {

            // [data-lk-modal] open trigger (must be outside a backdrop to avoid re-triggering)
            const trigger = e.target.closest('[data-lk-modal]');
            if (trigger && !trigger.closest('.lk-modal-backdrop')) {
                e.preventDefault();
                open(trigger.dataset.lkModal);
                return;
            }

            // .lk-modal-close or [data-lk-modal-close] close button
            const closeBtn = e.target.closest('.lk-modal-close, [data-lk-modal-close]');
            if (closeBtn) {
                const backdrop = closeBtn.closest('.lk-modal-backdrop');
                if (backdrop) { close(backdrop.id); return; }
            }

            // Backdrop click (clicking outside the .lk-modal box)
            if (e.target.classList.contains('lk-modal-backdrop')) {
                close(e.target.id);
                return;
            }

            // Discard dialog: keep editing
            if (e.target.id === 'lkDiscardCancelBtn') {
                _hideDiscard();
                return;
            }

            // Discard dialog: confirm discard
            if (e.target.id === 'lkDiscardConfirmBtn') {
                if (_pendingClose) _doClose(_pendingClose);
                _hideDiscard();
                return;
            }
        });

        // ESC key closes the topmost open modal
        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            // Discard dialog takes priority
            const discard = document.getElementById('lkDiscardBackdrop');
            if (discard && discard.style.display === 'flex') {
                _hideDiscard();
                return;
            }
            const openBackdrop = document.querySelector('.lk-modal-backdrop[style*="grid"]');
            if (openBackdrop) close(openBackdrop.id);
        });

        // Dirty state: auto-watch input/change inside [data-lk-dirty-watch]
        document.addEventListener('input', (e) => {
            const watched = e.target.closest('[data-lk-dirty-watch]');
            if (!watched) return;
            const backdrop = watched.closest('.lk-modal-backdrop');
            if (backdrop) setDirty(backdrop.id, true);
        });

        document.addEventListener('change', (e) => {
            const watched = e.target.closest('[data-lk-dirty-watch]');
            if (!watched) return;
            const backdrop = watched.closest('.lk-modal-backdrop');
            if (backdrop) setDirty(backdrop.id, true);
        });
    });

    return { open, close, forceClose, setDirty };
})();

window.LkModal = LkModal;
